/**
 * /map の左右リストの幅。ドラッグで変えた幅を localStorage に残し、次回も同じ幅で開く。
 *
 * 上下限はサンプル (route-planner: pages/MapPage.tsx startLeftResize) と同じ。
 * Why 新設: 旧地図配車 (V3Plans/Presenter.tsx:387,407) は左 430・右 300 の固定値で、
 * 開閉で 0 に切り替えるだけのリサイズ機構しか持たない。流用できる実装が無い。
 */
export const MIN_PANEL_WIDTH = 260;
export const MAX_PANEL_WIDTH = 720;

/** 既定は変更前の固定値そのまま。初回表示の見た目を変えない */
export const DEFAULT_LEFT_PANEL_WIDTH = 360;
export const DEFAULT_RIGHT_PANEL_WIDTH = 340;

/** リサイズハンドルの幅。左右 1 本ずつ置く */
export const RESIZE_HANDLE_WIDTH = 6;

/**
 * 中央地図に残す最小幅。左右を独立して上限まで広げると、地図は flex: 1・minWidth: 0 なので
 * 幅 0 まで潰れる。幅は localStorage に残るため、一度潰すとリロードしても地図が出ない。
 * 1366px の画面で右が既定幅なら左は上限 720px まで伸ばせる値にしてある。
 */
export const MIN_MAP_WIDTH = 240;

/** このパネル以外に必要な幅（反対側のパネル＋ハンドル 2 本＋地図の最小幅） */
export function reservedWidthFor(otherPanelWidth: number): number {
  return otherPanelWidth + RESIZE_HANDLE_WIDTH * 2 + MIN_MAP_WIDTH;
}

export const LEFT_PANEL_WIDTH_STORAGE_KEY = 'map.leftPanelWidth';
export const RIGHT_PANEL_WIDTH_STORAGE_KEY = 'map.rightPanelWidth';

/** ドラッグ中の幅を上下限へ収める。端数は 1px 未満のちらつきになるので丸める */
export function clampPanelWidth(width: number): number {
  return Math.round(Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, width)));
}

/**
 * 器（3 ペインの親）へ収まる幅に落とす。保存値そのものは書き換えない。
 *
 * Why 描画時にも要る: ドラッグ開始時の上限だけでは
 *   右を広げる → 右を畳む → 左を広げる → 右を開き直す
 * の順で左右とも大きな保存値を持ててしまい、中央地図が幅 0 になる。幅は localStorage に
 * 残るのでリロードしても直らない。画面を狭めた場合・広い画面で保存した幅を狭い画面で
 * 開いた場合も同じ。
 *
 * Why not 保存値を書き換える: 画面を一時的に狭めただけで設定を失う。描画時に落とすだけに
 * すれば、広げ直したときに元の幅へ戻る。
 *
 * ## 削り方は「左を優先して残す」
 * 超過分を今の幅の比で削る実装にしていたときは、片側の保存値だけが変わると分母が動いた。
 * 補正中の左を 1px 右へドラッグすると（表示値 388 が保存される）左が 43px 縮んで触っていない
 * 右が広がる、というドラッグの向きと逆の跳ねが出た（Codex 指摘）。
 * 左の補正結果を右の保存値から独立させることで、この跳ねが原理的に起きないようにする。
 * 左を優先するのは、依頼の主題が左リストで納品先名称を読み切ることだから。
 */
export function fitPanelWidths(params: {
  /** 3 ペインを並べている親要素の幅。測れていなければ 0 */
  containerWidth: number;
  left: number;
  right: number;
  /** 右ペインが開いているか。畳んでいる間は右の幅を数えない */
  rightOpen: boolean;
}): { left: number; right: number } {
  const { containerWidth, left, right, rightOpen } = params;
  if (containerWidth <= 0) return { left, right };

  // ハンドルは右を畳んでいる間 1 本しか描かない（Presenter 側と同じ条件）
  const available = containerWidth - RESIZE_HANDLE_WIDTH * (rightOpen ? 2 : 1) - MIN_MAP_WIDTH;

  if (!rightOpen) {
    return { left: Math.max(MIN_PANEL_WIDTH, Math.min(left, available)), right };
  }

  if (left + right <= available) return { left, right };

  // 左は「右に下限を残せる範囲」まで保つ。ここで右の保存値を見ないのが跳ね防止の要点
  const fittedLeft = Math.max(MIN_PANEL_WIDTH, Math.min(left, available - MIN_PANEL_WIDTH));
  // 残りを右に回す。両方下限でも入らない画面では下限で止める
  // （地図の最小幅を諦める。パネルを消すほうが困る）
  const fittedRight = Math.max(MIN_PANEL_WIDTH, Math.min(right, available - fittedLeft));

  return { left: fittedLeft, right: fittedRight };
}

/**
 * localStorage 復元時のバリデータ（usePersistedJSON 用）。
 *
 * Why not 範囲外を clamp して救う: 上下限を変えたときに古い保存値が黙って新しい境界へ
 * 張り付く。既定へ戻せば「なぜその幅なのか」が読める。
 */
export function isPanelWidth(parsed: unknown): parsed is number {
  return typeof parsed === 'number'
    && Number.isFinite(parsed)
    && parsed >= MIN_PANEL_WIDTH
    && parsed <= MAX_PANEL_WIDTH;
}
